// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { DefaultButton } from 'office-ui-fabric-react/lib/Button';
import { DirectionalHint } from 'office-ui-fabric-react/lib/ContextualMenu';

import { isUserSuperAdmin, isAccountAdmin, isShopAdmin } from './../../helpers/authorization';
import history from 'src/history';
import LanguageSelectorModal from './../../components/LanguageSelectorModal';
import * as userTierConfig from './../../lib/userTierConfig';
import { logPageAction } from './../../lib/ApplicationInsights';
import { resetOnStartOver } from './../../store/action';
import { setUserTierData } from './../../store/user/userInfoReducer';
import { userAgentTierSelector, userInfoSelector } from './../../store/user/selector';

import Styles from './styles.module.scss';
import { msalInstance } from '../../../src/index';

enum ChangeableTier {
    consumerTier = 5,
    commercialTier = 15,
}

export default function UserConfigMenu() {
    const menuProps = {
        isBeakVisible: false,
        directionalHint: DirectionalHint.bottomLeftEdge,
        gapSpace: 0,
        beakWidth: 20,
        directionalHintFixed: true,
    };

    const { t } = useTranslation();
    const dispatch = useDispatch();

    const [showLanguageModal, updateLanguageModal] = React.useState(false);

    const userInfo = useSelector(state => userInfoSelector(state));
    const userTier = useSelector(state => userAgentTierSelector(state));
    const selectedTierName = userTierConfig.getTierName();
    const { firstName: loggedInUser = '' } = userInfo;

    const baseTierConfig = userTierConfig.getUserTierConfig(userInfo.tierName);
    // tslint:disable-next-line: triple-equals - matching number or string
    if (selectedTierName != userTier) {
        if (baseTierConfig.checkForAlternateTier(selectedTierName)) {
            dispatch(setUserTierData({ userInfo: { tierName: selectedTierName } }));
        } else {
            userTierConfig.setTierName(userInfo.tierName);
        }
    }

    const modalCallbacks = {
        onTierChange: () => {
            userInfo.tierName = (userTier === ChangeableTier.consumerTier.toString())
                ? ChangeableTier.commercialTier.toString()
                : ChangeableTier.consumerTier.toString();
            logPageAction('Change tier item selected in user menu', `tierSelected: ${userInfo.tierName.toString()}`);

            localStorage.setItem('agentSelectedTier', userInfo.tierName);
            dispatch({ type: 'SET_USER_TIER_DATA', payload: userInfo });
            dispatch(resetOnStartOver());
            if (history.location.pathname !== '/') {
                history.replace('/');
            }
        },
        closeLanguageModal: () => {
            logPageAction('Close language modal');
            updateLanguageModal(false);
        },
        openLanguageModal: () => {
            logPageAction('Language item selected in user menu');
            updateLanguageModal(true);
        },
    };

    const tierNameToDisplay = () => {
        switch (userTier.toString()) {
            case ChangeableTier.consumerTier.toString():
                return `(${t('consumer')})`;
            case ChangeableTier.commercialTier.toString():
                return `(${t('commercial')})`;
            default:
                return '';
        }
    };

    const buttonText = `${loggedInUser} ${tierNameToDisplay()}`;
    const valuesObj = {
        showLanguageModal, buttonText, menuProps, userInfo, modalCallbacks,
    };
    return renderUserConfigMenuView(valuesObj);
}

export interface RenderUserConfigMenuViewProps {
    showLanguageModal: any;
    buttonText: string;
    menuProps: any;
    userInfo: any;
    modalCallbacks: any;
}
export function renderUserConfigMenuView(props: RenderUserConfigMenuViewProps): JSX.Element {
    return (
        <div aria-hidden={props.showLanguageModal}>
            <DefaultButton
                text={props.buttonText}
                className={Styles.manageUser}
                menuProps={{
                    ...props.menuProps,
                    className: Styles.manageUserMenu,
                    items: ShowMenuSelection(props.userInfo, props.modalCallbacks),
                }}
            />
            <LanguageSelectorModal
                closeModal={props.modalCallbacks.closeLanguageModal}
                showModal={props.showLanguageModal}
            />
        </div>
    );// as any;//as JSX.IntrinsicElements;
}

function ShowMenuSelection(userInfo: any, modalCallbacks: any) {
    const { t } = useTranslation();

    const userTier = useSelector(state => userAgentTierSelector(state));
    const userTierChangeEnable = !!ChangeableTier[userTier];

    const roleName = userInfo.roleName || '';
    const isUserTierConsumer = userInfo.tierName === ChangeableTier.consumerTier.toString();
    // if user is a consumer then menu will display 'change to commercial' and vice versa.
    const tierName = isUserTierConsumer ? t('commercial') : t('consumer');

    const manageUserClick = () => {
        logPageAction('Manage Users item selected from user menu');
        history.push('manageUsers');
    };

    const managePartnersClick = () => {
        logPageAction('Manage Partners item selected from user menu');
        history.push('managePartners');
    };

    const signOutClick = () => {
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
            const logoutRequest = {
                account: accounts[0],
            };
            msalInstance.logoutRedirect(logoutRequest).catch(e => {
                console.error(e);
            });
        }
        else {
            msalInstance.logoutRedirect().catch(e => {
                console.error(e);
            });
        }
    };

    const signOutMenu = {
        key: 'sign_out',
        text: t('sign_out'),
        onClick: signOutClick,
    };

    const languageMenu = {
        key: 'language',
        text: t('language'),
        onClick: modalCallbacks.openLanguageModal,
    };

    const changeTierMenu = {
        key: 'change_tier',
        text: t('change_tier', { tierName }),
        onClick: modalCallbacks.onTierChange,
    };

    const manageUserMenu = {
        key: 'manage_users',
        text: t('manage_users'),
        onClick: manageUserClick,
    };

    const managePartnersMenu = {
        key: 'manage_partners',
        text: t('manage_partners'),
        onClick: managePartnersClick,
    };

    const menuSelections: any = [languageMenu];
    if (roleName) {
        // a user is logged in
        if (userTierChangeEnable) {
            menuSelections.push(changeTierMenu);
        }
        if (isUserSuperAdmin(userInfo) || isAccountAdmin(userInfo) || isShopAdmin(userInfo)) {
            menuSelections.push(manageUserMenu);
        }
        if (isUserSuperAdmin(userInfo)) {
            menuSelections.push(managePartnersMenu);
        }
        menuSelections.push(signOutMenu);
    }
    return menuSelections;
}